/**
 * useNetworkStatus Hook
 *
 * React hook for monitoring network status and syncing with Redux.
 *
 * Requirements: 5.4
 */

import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getNetworkStatusManager } from '../services/networkStatus'
import { getOperationQueueManager } from '../services/operationQueue'
import {
    setOnlineStatus,
    setQueuedOperations,
} from '../store/slices/networkSlice'
import type { RootState } from '../store'

/**
 * Hook return type
 */
export interface UseNetworkStatusReturn {
    /** Whether the app is currently online */
    isOnline: boolean

    /** Number of queued operations */
    queuedOperations: number

    /** Timestamp of last status change */
    lastStatusChange: number | null
}

/**
 * Hook to monitor network status
 *
 * Automatically syncs network status with Redux store and monitors
 * the operation queue size.
 */
export function useNetworkStatus(): UseNetworkStatusReturn {
    const dispatch = useDispatch()
    const networkState = useSelector((state: RootState) => state.network)

    useEffect(() => {
        const networkManager = getNetworkStatusManager()
        const queueManager = getOperationQueueManager()

        // Sync initial state
        dispatch(setOnlineStatus(networkManager.isOnline))
        dispatch(setQueuedOperations(queueManager.getQueueSize()))

        // Listen for network status changes
        const unsubscribe = networkManager.addListener((isOnline) => {
            dispatch(setOnlineStatus(isOnline))
        })

        // Poll queue size periodically (every 2 seconds)
        const queueSizeInterval = setInterval(() => {
            dispatch(setQueuedOperations(queueManager.getQueueSize()))
        }, 2000)

        return () => {
            unsubscribe()
            clearInterval(queueSizeInterval)
        }
    }, [dispatch])

    return {
        isOnline: networkState.isOnline,
        queuedOperations: networkState.queuedOperations,
        lastStatusChange: networkState.lastStatusChange,
    }
}
